import { useMutation, useQuery } from '@apollo/client';
import React, {
  useEffect, useMemo, useState,
} from 'react';
import { usePaginatedQuery, Loader } from '@playbookux/playbook-web-core';
import _, { debounce } from 'lodash';
import useDebounce from '../../../core/hooks/use-debounce';
import withDashboardSidebar from '../../components/with-dashboard-sidebar';
import CustomTable from './custom-table';

// components
import FeatureFlags from './feature-flags';
// graphql
import {
  COMPANIES_LIMIT,
  DEBOUNCE_TIME,
  GET_ALL_COMPANIES,
  ORDER_COMPANIES_BY,
  GET_ALL_PRICES,
  CHANGE_PRICE,
  DELETE_PRICE,
  SET_COMPANY_FEATURE_FLAG,
  SET_GLOBAL_FEATURE_FLAG,
} from './graphql';
// styles
import {
  CenteredLoaderContainer, Dropdown, FeatureFlagLine, Header, Message,
} from './styles';
// constants

import { useAllFeatureFlagsWithAttrs, useFeatureFlagsWithAttrs } from '../../../core/hooks/feature-flags';

const PRODUCTSKEYS = {
  unmoderated: 'Unmoderated',
  moderated_30: 'Moderated 30 min',
  moderated_60: 'Moderated 60 min',
  moderated_90: 'Moderated 90 min',
  moderated_120: 'Moderated 120 min',
  card_sort: 'Card Sort',
  tree_test: 'Tree Test',
  survey: 'Survey',
  upload: 'Upload Research',
};

const GLOBAL_COMPANY = 'default';

function Pricing() {
  // searches
  const [selectedCompany, setSelectedCompany] = useState('default');
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedCompanySearch] = useDebounce(() => companySearch, DEBOUNCE_TIME, [companySearch]);

  const {
    loading: loadingPricing,
    data: pricingDataRaw = {},
    error: pricingError,
  } = useQuery(GET_ALL_PRICES, {
    variables: {
      companyId: selectedCompany === 'default' ? null : selectedCompany,
    },
    fetchPolicy: 'network-only',
  });

  const pricingData = useMemo(() => {
    if (pricingDataRaw && pricingDataRaw.allCompanyPrices) {
      return pricingDataRaw.allCompanyPrices?.reduce((acc, price) => {
        acc[PRODUCTSKEYS[price.productType]] = {
          price: price.price,
          key: price.productType,
          company: price.company?.id || 'default',
        };
        return acc;
      }, {});
    }
    return {};
  }, [pricingDataRaw]);

  const [changePrice, { loading: changePriceLoading, error: changePriceError }] = useMutation(CHANGE_PRICE, {
    refetchQueries: [
      {
        query: GET_ALL_PRICES,
        variables: {
          companyId: selectedCompany === 'default' ? null : selectedCompany,
        },
      },
    ],
  });

  const [deletePrice, { loading: deletePriceLoading, error: deletePriceError }] = useMutation(DELETE_PRICE, {
    refetchQueries: [
      {
        query: GET_ALL_PRICES,
        variables: {
          companyId: selectedCompany === 'default' ? null : selectedCompany,
        },
      },
    ],
  });

  // companies
  const attributes = {
    variables: {
      limit: COMPANIES_LIMIT,
      offset: 0,
      order: ORDER_COMPANIES_BY,
      search: debouncedCompanySearch,
    },
  };

  const {
    data: companyData = [],
    loading: loadingCompanies,
  } = usePaginatedQuery(GET_ALL_COMPANIES, attributes, { dataField: 'companies' });

  const workspaces = useMemo(() => {
    let newOptions = [];
    if (companyData && companyData.companies) {
      newOptions = companyData.companies.map((company) => ({
        key: company.id,
        value: company.id,
        text: company.name,
      }));
    }
    // add default option as the first one
    newOptions.unshift({
      key: '',
      value: 'default',
      text: 'Default',
    });
    return newOptions;
  }, [companyData]);

  const handleSearchChange = (__, { searchQuery = '' }) => {
    setCompanySearch(searchQuery.trim());
  };

  const handleChangeWorkspace = (value) => {
    if (value === '') {
      setSelectedCompany('default');
      setCompanySearch('');
    } else {
      setSelectedCompany(value);
    }
  };

  // Feature Flags

  const allFeatureFlags = useAllFeatureFlagsWithAttrs();

  const companyFeatureFlagsInitialState = useFeatureFlagsWithAttrs(selectedCompany);

  const [companyFeatureFlags, setCompanyFeatureFlags] = useState(companyFeatureFlagsInitialState);
  const [companyFeatureFlagsErrors, setCompanyFeatureFlagsErrors] = useState({});

  useEffect(() => {
    if (!_.isEqual(companyFeatureFlagsInitialState?.featureFlags, companyFeatureFlags?.featureFlags)) {
      setCompanyFeatureFlags({
        ...companyFeatureFlagsInitialState,
        ...companyFeatureFlagsInitialState?.featureFlags,
      });
    }
  }, [companyFeatureFlags?.featureFlags, companyFeatureFlagsInitialState]);

  const [setCompanyFeatureFlag] = useMutation(SET_COMPANY_FEATURE_FLAG);
  const [setGlobalFeatureFlag] = useMutation(SET_GLOBAL_FEATURE_FLAG);

  // todo: have the submit be called only once if multiple checkbox clicks are made
  const submitFeatureFlag = debounce(async (featureFlag) => {
    try {
      setCompanyFeatureFlagsErrors((prevErrors) => ({
        ...prevErrors,
        [featureFlag.name]: '',
      }));
      await (selectedCompany === GLOBAL_COMPANY ? setGlobalFeatureFlag : setCompanyFeatureFlag)({
        variables: {
          companyId: selectedCompany === GLOBAL_COMPANY ? undefined : selectedCompany,
          featureFlagId: featureFlag.id,
          enabled: !featureFlag.enabled,
        },
      });
    } catch (e) {
      // revert the checkbox status
      setCompanyFeatureFlags((prevFlags) => ({
        ...prevFlags,
        [featureFlag.name]: {
          ...prevFlags[featureFlag.name],
          enabled: !prevFlags[featureFlag.name].enabled,
        },
      }));
      setCompanyFeatureFlagsErrors((prevErrors) => ({
        ...prevErrors,
        [featureFlag.name]: 'Error when changing feature flag',
      }));
    }
  }, 500);

  const featureFlagsWithSelections = useMemo(() => {
    const allFeatureFlagsClone = _.cloneDeep(allFeatureFlags);
    Object.keys(allFeatureFlagsClone).forEach((featureFlagName) => {
      allFeatureFlagsClone[featureFlagName].enabled = companyFeatureFlags[featureFlagName]?.enabled || false;
    });
    return allFeatureFlagsClone;
  }, [allFeatureFlags, companyFeatureFlags]);

  const onToggleFeatureFlag = async (featureFlagName) => {
    setCompanyFeatureFlags((prevFlags) => ({
      ...prevFlags,
      [featureFlagName]: {
        ...prevFlags[featureFlagName],
        enabled: !prevFlags[featureFlagName].enabled,
      },
    }));
    submitFeatureFlag({ ...featureFlagsWithSelections[featureFlagName], name: featureFlagName });
  };

  const featureFlag = () => (
    <>
      {selectedCompany !== GLOBAL_COMPANY && (
        <FeatureFlagLine>
          {
            Object.keys(featureFlagsWithSelections).length > 0
            && (
              <FeatureFlags
                featureFlagsErrors={companyFeatureFlagsErrors}
                featureFlags={featureFlagsWithSelections}
                onChangeFeatureFlags={onToggleFeatureFlag}
              />
            )
          }
        </FeatureFlagLine>
      )}
    </>
  );

  // end of feature flags

  return (
    <>
      <Header>
        <Dropdown
          loading={loadingCompanies}
          onChange={(__, { value }) => handleChangeWorkspace(value)}
          onSearchChange={handleSearchChange}
          options={workspaces}
          clearable={selectedCompany !== 'default'}
          search
          selection
          value={selectedCompany}
        />

      </Header>
      {loadingPricing && (
        <CenteredLoaderContainer>
          <Loader data-loader="pricing-loader" />
        </CenteredLoaderContainer>
      )}
      {pricingError && (
        <Message negative>
          <Message.Header>Something went wrong</Message.Header>
        </Message>
      )}
      {changePriceError && (
        <Message negative>
          <Message.Header>Error when changing price</Message.Header>
        </Message>
      )}
      {deletePriceError && (
        <Message negative>
          <Message.Header>Error when resetting price to default</Message.Header>
        </Message>
      )}
      {selectedCompany && !loadingPricing && (
        <CustomTable
          pricing={pricingData}
          company={selectedCompany}
          changePrice={changePrice}
          deletePrice={deletePrice}
          loadingDelete={deletePriceLoading}
          loadingChange={changePriceLoading}
          featureFlag={featureFlag}
        />
      )}
    </>
  );
}

export default withDashboardSidebar(Pricing);
